import { FC, useMemo, useState } from 'react';

import { LockClosedIcon } from '@heroicons/react/outline';
import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { BucketImg } from '@shared/components/BucketImg';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import {
  getCouponShareStatusLabel,
  getCouponShareStatusStyle,
} from '../utils/giftOrderCoupon.utils';
import { ReshareWarningModal } from './ReshareWarningModal';
import { ShareStatusBadge } from './ShareStatusBadge';

interface CouponRowProps {
  coupon: GiftCouponForStore;
  onShare: (coupon: GiftCouponForStore) => void;
  onMemo: (coupon: GiftCouponForStore) => void;
  onAdditionalPhoneEdit: (couponId: string, phone: string) => Promise<void>;
  /**
   * 잠금 번호 수정/추가 모달 오픈. 공유된 쿠폰 또는 회수로 잠금된 쿠폰에서 노출.
   */
  onOpenPhoneLockEdit?: (coupon: GiftCouponForStore) => void;
  isSharing: boolean;
  /**
   * 컴팩트 모드. 전달완료 리스트처럼 행이 많을 때 이미지를 숨기고
   * 패딩을 줄여 한눈에 더 많이 들어오도록 표시한다.
   */
  compact?: boolean;
}

const formatConfirmedAt = (date: Date | string) => {
  const d = new Date(date);
  return `${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')} 완료`;
};

export const CouponRow: FC<CouponRowProps> = ({
  coupon,
  onShare,
  onMemo,
  onAdditionalPhoneEdit,
  onOpenPhoneLockEdit,
  isSharing,
  compact = false,
}) => {
  const giftItem = coupon.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  const imageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  const isShared = coupon.shareStatus !== GiftCouponShareStatus.NotShared;

  const isRedeemed =
    coupon.redeemStatus === GiftCouponRedeemStatus.Delivered ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Refunded ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Canceled;

  const shareDisabled = isRedeemed || coupon.shareStatus === GiftCouponShareStatus.Viewed;

  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  const isAdditional = !!coupon.isAdditional;
  const isReissued = !!coupon.isReissued;
  const needsCarefulShare = isAdditional || isReissued;

  // 잠금 진입점 노출 조건:
  //   - 미수령(=아직 사용 가능) 상태
  //   - 공유된 적 있거나(예방 잠금 추가) / 이미 잠금 번호가 있거나(사후 수정)
  //   - carefulShare 쿠폰은 자체 입력 UI 가 있어 중복 노출 방지 (공유 후에는 노출)
  const lockedPhone = coupon.additionalRecipientPhone;
  // 수령자가 이미 본인 번호를 인증·등록한 경우(verification.phoneNumber 존재, 또는
  // 한 단계 더 진행된 교환 신청자 recipient.phoneNumber 존재)에는 수신번호를
  // 잠그거나 변경할 수 없으므로 진입점을 노출하지 않는다.
  const hasVerifiedRecipient =
    !!coupon.verification?.phoneNumber || !!coupon.recipient?.phoneNumber;
  const showLockEntry =
    !!onOpenPhoneLockEdit &&
    !isRedeemed &&
    !hasVerifiedRecipient &&
    (!!lockedPhone || (isShared && !needsCarefulShare));

  const [localConfirmReset, setLocalConfirmReset] = useState(false);

  const effectiveConfirmedAt = localConfirmReset ? null : coupon.confirmedAt;

  // 카카오톡으로 실제 전달된 기록(웹훅 확인 시각 또는 카카오 공유 횟수)이 있으면
  // "다시 전달" 시 중복 발송 경고 모달을 먼저 띄운다.
  const hasKakaoShareRecord = !!coupon.kakaoSharedAt || (coupon.shareKakaoCount ?? 0) > 0;
  const [showReshareWarning, setShowReshareWarning] = useState(false);

  const handleReshareClick = () => {
    if (hasKakaoShareRecord) {
      setShowReshareWarning(true);
    } else {
      onShare(coupon);
    }
  };

  const [recipientPhone, setRecipientPhone] = useState(
    coupon.additionalRecipientPhone || '',
  );
  const [recipientMemo, setRecipientMemo] = useState('');
  const [isSavingRecipient, setIsSavingRecipient] = useState(false);

  const handleSaveRecipientAndShare = async () => {
    const phone = recipientPhone.replace(/[^0-9]/g, '');
    if (!phone) {
      alert('수신자 휴대폰번호를 입력해주세요.');
      return;
    }
    setIsSavingRecipient(true);
    try {
      await onAdditionalPhoneEdit(coupon._id, phone);
      onShare(coupon);
    } catch {
      alert('수신자 정보 저장에 실패했습니다.');
    } finally {
      setIsSavingRecipient(false);
    }
  };

  return (
    <div
      className={`rounded-lg bg-white shadow-sm ${compact ? 'px-3 py-2' : 'p-3'} ${
        isShared ? 'ring-1 ring-green-400' : ''
      } ${needsCarefulShare ? 'bg-amber-50/30 ring-1 ring-amber-300' : ''}`}
    >
      <div className={`flex items-center ${compact ? 'gap-2' : 'gap-3'}`}>
        {!compact && (
          <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
            {imageUrl ? (
              <BucketImg
                value={imageUrl}
                size="w200"
                alt={itemName || '쿠폰'}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                No Image
              </div>
            )}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`font-bold text-purple-600 ${compact ? 'text-base' : 'text-sm'}`}
            >
              #{coupon.seq}
            </span>

            <span className={`text-gray-600 ${compact ? 'text-sm' : 'text-xs'}`}>
              {coupon.code}
            </span>
            {needsCarefulShare && (
              <span
                className={`rounded bg-amber-100 px-1.5 py-0.5 font-bold text-amber-700 ${
                  compact ? 'text-xs' : 'text-[10px]'
                }`}
              >
                {isReissued ? '재발급' : '재발행'}
              </span>
            )}
            <ShareStatusBadge
              label={getCouponShareStatusLabel(coupon.shareStatus)}
              styleClass={getCouponShareStatusStyle(coupon.shareStatus)}
              channel={coupon.lastSharedChannel}
              kakaoCount={coupon.shareKakaoCount}
              size={compact ? 'lg' : 'md'}
            />
          </div>
          <p
            className={`mt-0.5 truncate font-medium text-gray-800 ${
              compact ? 'text-base' : 'text-sm'
            }`}
          >
            {itemName}
          </p>
        </div>

        {isShared ? (
          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            {effectiveConfirmedAt ? (
              <>
                <button
                  onClick={() => onMemo(coupon)}
                  className={`max-w-[140px] truncate rounded-lg bg-purple-50 font-medium text-purple-600 active:bg-purple-100 ${
                    compact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm'
                  }`}
                >
                  {coupon.memo || '메모 남기기'}
                </button>
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        '혹시 확인을 실수로 누르셨나요?\n예를 누르면 다시 전달할 수 있습니다.',
                      )
                    ) {
                      setLocalConfirmReset(true);
                    }
                  }}
                  className={`text-gray-400 underline decoration-dotted active:text-gray-600 ${
                    compact ? 'text-xs' : 'text-[10px]'
                  }`}
                >
                  {formatConfirmedAt(effectiveConfirmedAt)}
                </button>
              </>
            ) : (
              // 메모 남기기와 다시 전달을 한 줄이 아니라 위/아래 세로 정렬
              <div className="flex flex-col items-end gap-1.5">
                <button
                  onClick={() => onMemo(coupon)}
                  className={`rounded-lg px-4 py-2 text-sm active:bg-purple-100 ${
                    coupon.memo
                      ? 'max-w-[140px] truncate bg-purple-50 font-medium text-purple-600'
                      : 'border border-dashed border-purple-300 text-purple-400'
                  }`}
                >
                  {coupon.memo || '메모 남기기'}
                </button>
                {!shareDisabled && (
                  <button
                    onClick={handleReshareClick}
                    disabled={isSharing}
                    className={`flex-shrink-0 rounded-xl bg-green-100 px-4 py-2 text-sm font-bold text-green-700 active:bg-green-200 ${
                      isSharing ? 'opacity-50' : ''
                    }`}
                  >
                    {isSharing ? '...' : '다시 전달'}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          !shareDisabled &&
          !needsCarefulShare && (
            <button
              onClick={() => onShare(coupon)}
              disabled={isSharing}
              className={`flex-shrink-0 rounded-xl bg-purple-600 px-5 py-2.5 text-sm font-bold text-white transition active:bg-purple-700 ${
                isSharing ? 'opacity-50' : ''
              }`}
            >
              {isSharing ? '...' : '전달하기'}
            </button>
          )
        )}
      </div>
      {/* 잠금된 번호 뱃지 / 잠금 추가 진입점 */}
      {showLockEntry && (
        <button
          onClick={() => onOpenPhoneLockEdit?.(coupon)}
          className={`mt-2.5 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs active:opacity-80 ${
            lockedPhone
              ? 'bg-amber-50 text-amber-800'
              : 'border border-dashed border-gray-300 text-gray-500'
          }`}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <LockClosedIcon
              className={`h-3.5 w-3.5 flex-shrink-0 ${
                lockedPhone ? 'text-amber-600' : 'text-gray-400'
              }`}
            />
            {lockedPhone ? (
              <span className="truncate">
                <span className="font-bold">
                  {phoneNumberStringFormator(lockedPhone)}
                </span>
                <span className="ml-1 text-amber-600">님만 받을 수 있어요</span>
              </span>
            ) : (
              <span>받는 분 지정하기</span>
            )}
          </span>
          <span
            className={`flex-shrink-0 text-[11px] font-medium ${
              lockedPhone ? 'text-amber-700 underline' : 'text-gray-500 underline'
            }`}
          >
            {lockedPhone ? '변경' : '지정'}
          </span>
        </button>
      )}
      {/* 재발행 쿠폰: 수신자 정보 입력 후 전달 */}
      {needsCarefulShare && !shareDisabled && !isShared && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="mb-2 text-[11px] text-amber-700">
            <span className="font-bold text-amber-900">휴대폰번호를 먼저 입력</span>한 뒤
            전달해주세요. 성함은 선택입니다.
          </p>
          <div className="flex flex-col gap-2">
            <input
              type="tel"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="받으시는 분 휴대폰번호"
              className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              maxLength={13}
            />
            <input
              type="text"
              value={recipientMemo}
              onChange={(e) => setRecipientMemo(e.target.value)}
              placeholder="받으시는 분 성함 (선택)"
              className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            <button
              onClick={handleSaveRecipientAndShare}
              disabled={isSavingRecipient}
              className="w-full rounded-xl bg-amber-600 py-2.5 text-sm font-bold text-white active:bg-amber-700 disabled:opacity-50"
            >
              {isSavingRecipient ? '저장 중...' : '정보 저장 후 전달하기'}
            </button>
          </div>
        </div>
      )}

      <ReshareWarningModal
        open={showReshareWarning}
        onConfirm={() => {
          setShowReshareWarning(false);
          onShare(coupon);
        }}
        onClose={() => setShowReshareWarning(false)}
      />
    </div>
  );
};
